'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import ContactActions from '@/components/common/ContactActions';

interface MechanicCardProps {
  mechanic: {
    id: string;
    name: string;
    image: string;
    imageAlt: string;
    rating: number;
    reviewCount: number;
    distance: number;
    specializations: string[];
    services: string[];
    isVerified: boolean;
    phoneNumber: string;
    address: string;
    reviewHighlight: string;
    availability: string;
  };
}

const MechanicCard = ({ mechanic }: MechanicCardProps) => {
  return (
    <div className="bg-card rounded-xl shadow-elevation-md overflow-hidden transition-smooth hover:shadow-elevation-lg">
      <div className="flex flex-col lg:flex-row">
        {/* Image Section */}
        <div className="relative w-full lg:w-48 h-48 lg:h-auto overflow-hidden flex-shrink-0">
          <AppImage
            src={mechanic.image}
            alt={mechanic.imageAlt}
            className="w-full h-full object-cover"
          />
          {mechanic.isVerified && (
            <div className="absolute top-3 left-3 flex items-center space-x-1 bg-success text-success-foreground rounded-full px-3 py-1 shadow-elevation-sm">
              <Icon name="CheckBadgeIcon" size={16} variant="solid" />
              <span className="font-caption text-xs font-medium">Verified</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex-1 p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Left Content */}
            <div className="flex-1 space-y-3">
              {/* Header */}
              <div>
                <Link
                  href={`/mechanic-detail?id=${mechanic.id}`}
                  className="font-heading text-xl font-semibold text-text-primary hover:text-primary transition-smooth inline-block"
                >
                  {mechanic.name}
                </Link>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon
                        key={star}
                        name="StarIcon"
                        size={16}
                        variant={star <= mechanic.rating ? 'solid' : 'outline'}
                        className={star <= mechanic.rating ? 'text-accent' : 'text-muted-foreground'}
                      />
                    ))}
                    <span className="font-body text-sm font-medium text-text-primary ml-2">
                      {mechanic.rating.toFixed(1)}
                    </span>
                    <span className="font-caption text-xs text-muted-foreground">
                      ({mechanic.reviewCount} reviews)
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-text-secondary">
                    <Icon name="MapPinIcon" size={16} />
                    <span className="font-body text-sm">{mechanic.distance} mi</span>
                  </div>
                </div>
              </div>

              {/* Specializations */}
              <div className="flex flex-wrap gap-2">
                {mechanic.specializations.map((spec) => (
                  <span
                    key={spec}
                    className="inline-flex items-center space-x-1 bg-primary/10 text-primary rounded-full px-3 py-1 font-caption text-xs font-medium"
                  >
                    <Icon name="WrenchScrewdriverIcon" size={14} />
                    <span>{spec}</span>
                  </span>
                ))}
              </div>

              {/* Services */}
              <div>
                <p className="font-caption text-xs font-medium text-text-secondary mb-2">
                  Key Services:
                </p>
                <div className="flex flex-wrap gap-2">
                  {mechanic.services.slice(0, 4).map((service) => (
                    <span
                      key={service}
                      className="inline-flex items-center bg-muted text-text-secondary rounded-md px-2 py-1 font-body text-xs"
                    >
                      {service}
                    </span>
                  ))}
                  {mechanic.services.length > 4 && (
                    <span className="inline-flex items-center text-primary rounded-md px-2 py-1 font-body text-xs font-medium">
                      +{mechanic.services.length - 4} more
                    </span>
                  )}
                </div>
              </div>

              {/* Review Highlight */}
              <div className="bg-muted rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="ChatBubbleLeftIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="font-body text-sm text-text-secondary italic leading-relaxed">
                    "{mechanic.reviewHighlight}"
                  </p>
                </div>
              </div>

              {/* Availability */}
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={16} className="text-success" />
                <span className="font-body text-sm text-text-secondary">{mechanic.availability}</span>
              </div>
            </div>

            {/* Right Actions */}
            <div className="lg:w-64 flex-shrink-0">
              <ContactActions
                variant="card"
                phoneNumber={mechanic.phoneNumber}
                address={mechanic.address}
                mechanicName={mechanic.name}
                className="lg:flex-col lg:space-x-0 lg:space-y-2"
              />
              <Link
                href={`/mechanic-detail?id=${mechanic.id}`}
                className="mt-3 flex items-center justify-center space-x-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97] w-full"
              >
                <Icon name="InformationCircleIcon" size={16} />
                <span>View Details</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MechanicCard;